import { useState } from "react";
import SectionCard from "@/components/SectionCard";
import KpiCard from "@/components/KpiCard";
import BarChart from "@/components/charts/BarChart";
import BoxPlotChart from "@/components/charts/BoxPlotChart";
import HeatmapChart from "@/components/charts/HeatmapChart";
import AreaChart from "@/components/charts/AreaChart";
import { tiemposInternos, DATOS_POR_PERIODO } from "@/mocks/data";
import { useFilters } from "@/context/FiltersContext";
import { formatNum } from "@/lib/constants";
import { Clock, Timer, TrendingDown, Hourglass, Activity, Beaker, CreditCard } from "lucide-react";

const PROCESOS = [
  { key: "consulta", label: "Consulta Médica", color: "#06B6D4", mock: true },
  { key: "cirugia", label: "Cirugía / Pabellón", color: "#84CC16", mock: false },
  { key: "urgencia", label: "Urgencia", color: "#F97316", mock: false },
  { key: "laboratorio", label: "Laboratorio UTM", color: "#14B8A6", mock: true },
];

export default function Tiempos() {
  const [proc, setProc] = useState(PROCESOS[0]);
  const { filters } = useFilters();
  const periodoData = DATOS_POR_PERIODO[filters.periodo] || DATOS_POR_PERIODO["Abril 2026"];

  const d = tiemposInternos[proc.key];
  const boxData = d.etapas.map((etapa, i) => ({
    etapa,
    p25: d.p25[i],
    promedio: d.promedio_min[i],
    p75: d.p75[i],
    p95: d.p95[i],
  }));

  const total = d.promedio_min.reduce((a, b) => a + b, 0);
  const p95total = d.p95.reduce((a, b) => a + b, 0);

  const heatSeries = tiemposInternos.heatmap_hora_dia.dias.map((dia, i) => ({
    name: dia,
    data: tiemposInternos.heatmap_hora_dia.horas.map((h, j) => ({
      x: h,
      y: tiemposInternos.heatmap_hora_dia.data[i][j],
    })),
  }));

  return (
    <div className="space-y-6" data-testid="tiempos-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Tiempos Internos</h1>
        <p className="text-sm text-slate-500 mt-1">Visualización de tiempos paso a paso · {filters.periodo}</p>
      </div>

      {/* KPI row — datos reales del período */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          testid="kpi-prm-citas"
          label="Citas PRM"
          value={formatNum(periodoData.prm.total_citas)}
          icon={Activity}
          accent="violet"
        />
        <KpiCard
          testid="kpi-dg-consultas"
          label="Consultas DG"
          value={formatNum(periodoData.dg.episodios)}
          icon={Beaker}
          accent="blue"
        />
        <KpiCard
          testid="kpi-modulab-ordenes"
          label="Órdenes Modulab"
          value={formatNum(periodoData.modulab.ordenes)}
          icon={CreditCard}
          accent="emerald"
        />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="proceso-selector">
        {PROCESOS.map((p) => (
          <button
            key={p.key}
            data-testid={`proceso-${p.key}`}
            onClick={() => setProc(p)}
            className={`px-4 h-9 rounded-xl text-xs font-semibold transition border ${
              proc.key === p.key
                ? "text-white border-transparent shadow-md"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            style={proc.key === p.key ? { backgroundColor: p.color } : {}}
          >
            {p.label}
            {p.mock ? " ⚠️ Estimado" : " ✓ Dato real"}
          </button>
        ))}
      </div>

      {/* Nota de origen de datos */}
      {proc.mock ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
          <span>⚠️</span> Datos estimados — {proc.label} no tiene datos reales de tiempos en Redshift
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
          <span>✓</span> Dato real — Percentiles calculados desde {proc.key === "cirugia" ? "3.343" : "2.645"} registros CSV
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-tiempo-promedio" label="Tiempo promedio total" value={total} suffix="min" icon={Clock} accent="violet" />
        <KpiCard testid="kpi-tiempo-p95" label="P95 (percentil 95)" value={p95total} suffix="min" icon={Timer} accent="rose" />
        <KpiCard testid="kpi-etapa-critica" label="Etapa más larga" value={d.etapas[d.promedio_min.indexOf(Math.max(...d.promedio_min))]} icon={Hourglass} accent="amber" />
        <KpiCard testid="kpi-mejora" label="Mejora vs mes anterior" value="-8" suffix="%" icon={TrendingDown} accent="emerald" delta={-8} />
      </div>

      <SectionCard
        title="Distribución por etapa"
        subtitle={`Box plot: P25 · Promedio · P75 · P95${proc.mock ? " · ⚠️ Datos estimados" : " · Dato real"}`}
        testid="boxplot-etapas"
      >
        <BoxPlotChart data={boxData} color={proc.color} height={360} />
      </SectionCard>

      <SectionCard
        title="Comparación de percentiles"
        subtitle={`P25 · Promedio · P75 · P95 por etapa${proc.mock ? " · ⚠️ Datos estimados" : " · Dato real"}`}
        testid="percentiles-comparacion"
      >
        <BarChart
          categories={d.etapas}
          series={[
            { name: "P25", data: d.p25 },
            { name: "Promedio", data: d.promedio_min },
            { name: "P75", data: d.p75 },
            { name: "P95", data: d.p95 },
          ]}
          colors={["#94A3B8", proc.color, "#7C3AED", "#EF4444"]}
          height={340}
        />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionCard title="Carga por hora y día de la semana" subtitle="Atenciones por franja horaria · ⚠️ Datos estimados" testid="heatmap-hora-dia">
            <HeatmapChart
              categories={tiemposInternos.heatmap_hora_dia.horas}
              series={heatSeries}
              height={320}
              colorRange={{
                ranges: [
                  { from: 0, to: 20, color: "#F1F5F9" },
                  { from: 21, to: 35, color: "#DDD6FE" },
                  { from: 36, to: 45, color: "#A78BFA" },
                  { from: 46, to: 55, color: "#7C3AED" },
                  { from: 56, to: 100, color: "#4C1D95" },
                ],
              }}
            />
          </SectionCard>
        </div>
        <SectionCard title="Tendencia semanal" subtitle="Promedio vs P95 · ⚠️ Datos estimados" testid="tendencia-semanal">
          <AreaChart
            categories={tiemposInternos.tendencia_semanal.map((t) => t.semana)}
            series={[
              { name: "Promedio (min)", data: tiemposInternos.tendencia_semanal.map((t) => t.promedio) },
              { name: "P95 (min)", data: tiemposInternos.tendencia_semanal.map((t) => t.p95) },
            ]}
            colors={["#7C3AED", "#F97316"]}
            height={320}
          />
        </SectionCard>
      </div>

      <SectionCard
        title="Detalle de etapas"
        subtitle={`${proc.label} · ${filters.periodo}${proc.mock ? " · ⚠️ Datos estimados" : " · Dato real"}`}
        testid="detalle-etapas"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <th className="py-3 px-4">Etapa</th>
                <th className="py-3 px-4 text-right">P25</th>
                <th className="py-3 px-4 text-right">Promedio</th>
                <th className="py-3 px-4 text-right">P75</th>
                <th className="py-3 px-4 text-right">P95</th>
              </tr>
            </thead>
            <tbody>
              {boxData.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition">
                  <td className="py-3 px-4 font-semibold text-slate-800">{r.etapa}</td>
                  <td className="py-3 px-4 text-right text-slate-600 tabular-nums">{r.p25} min</td>
                  <td className="py-3 px-4 text-right font-semibold text-slate-900 tabular-nums">{r.promedio} min</td>
                  <td className="py-3 px-4 text-right text-slate-600 tabular-nums">{r.p75} min</td>
                  <td className="py-3 px-4 text-right text-rose-600 font-semibold tabular-nums">{r.p95} min</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
